import { motion, type MotionValue, useTransform } from 'framer-motion';
import {
    BriefcaseBusiness,
    ChartColumnIncreasing,
    ClipboardList,
    Cog,
    createLucideIcon,
    Gavel,
    Handshake,
    type LucideIcon,
    Newspaper,
    Truck,
    Users,
} from 'lucide-react';
import { type CSSProperties, forwardRef, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from 'react';
import { OilRig } from '../wop/icons';
import { Podium } from './Podium';
import { PropLayer } from './PropLayer';
import { ringLayout, slotOffset, tileLayout, type TileLayout, tilePose, wrapIndex } from './orbit-math';
import type { HeroIcon, HeroSection } from './types';
import { useOrbitPosition } from './useOrbitPosition';

const OilRigIcon = createLucideIcon('OilRig', OilRig);

const ICONS: Record<HeroIcon, LucideIcon> = {
    agents: Users,
    investments: ChartColumnIncreasing,
    drilling: OilRigIcon,
    business: Handshake,
    marketplace: BriefcaseBusiness,
    tenders: ClipboardList,
    manufacturers: Cog,
    logistics: Truck,
    auctions: Gavel,
    news: Newspaper,
};

function TileFace({ item }: { item: HeroSection }) {
    const Icon = ICONS[item.id];

    return (
        <span className="wop-orbit__tile-face">
            {Icon && <Icon className="wop-orbit__tile-icon" aria-hidden="true" strokeWidth={1.7} />}
            <span className="wop-orbit__tile-title">{item.title}</span>
            {item.subtitle && <span className="wop-orbit__tile-subtitle">{item.subtitle}</span>}
        </span>
    );
}

interface TileProps {
    item: HeroSection;
    slot: number;
    position: MotionValue<number>;
    layout: TileLayout;
    isActive: boolean;
    isPrimary: boolean;
    onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

function Tile({ item, slot, position, layout, isActive, isPrimary, onClick }: TileProps) {
    const pose = useTransform(position, (latest) => tilePose(slotOffset(slot, latest, layout.slotCount), layout));
    const transform = useTransform(pose, (p) => `rotateY(${p.rotateY}deg) translateZ(${layout.radius}px)`);
    const opacity = useTransform(pose, (p) => p.opacity);
    const zIndex = useTransform(pose, (p) => p.zIndex);
    const pointerEvents = useTransform(pose, (p) => (p.opacity < 0.15 ? 'none' : 'auto'));

    return (
        <motion.a
            id={isPrimary ? `wop-orbit-item-${item.id}` : undefined}
            href={item.href}
            role="option"
            tabIndex={-1}
            draggable={false}
            aria-hidden={!isPrimary || undefined}
            aria-selected={isActive}
            aria-label={item.title}
            className={`wop-orbit__tile${isActive ? ' is-active' : ''}`}
            style={{ transform, opacity, zIndex, pointerEvents, width: layout.tileW, height: layout.tileH }}
            onClick={onClick}
        >
            <TileFace item={item} />
        </motion.a>
    );
}

interface OrbitalCarouselProps {
    items: HeroSection[];
    initialIndex: number;
    radius?: number;
    flatten?: number;
    /** Items per second the ring drifts on its own; it stops while hovered. */
    driftSpeed?: number;
    label: string;
    assetBase: string;
    onSelect?: (item: HeroSection, index: number) => void;
}

export const OrbitalCarousel = forwardRef<HTMLDivElement, OrbitalCarouselProps>(function OrbitalCarousel(
    { items, initialIndex, radius = 560, flatten = 0.2, driftSpeed = 0.12, label, assetBase, onSelect },
    forwardedRef,
) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
            rootRef.current = node;

            if (typeof forwardedRef === 'function') {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        },
        [forwardedRef],
    );
    const [isHovered, setIsHovered] = useState(false);
    const [width, setWidth] = useState(0);
    const ring = useMemo(() => ringLayout(width, radius, flatten), [width, radius, flatten]);
    const layout = useMemo(() => tileLayout(ring.rx, flatten, ring.wall, items.length), [ring.rx, ring.wall, flatten, items.length]);

    const { position, activeIndex, isDrifting, goToIndex, stepBy, consumeDragClick, onPointerDown, onPointerMove, onPointerUp } = useOrbitPosition({
        itemCount: items.length,
        initialIndex: wrapIndex(initialIndex, Math.max(items.length, 1)),
        driftSpeed,
        isPaused: isHovered,
    });

    // The section facing the viewer is the "selected" one, also while drifting.
    const selectCallback = useRef(onSelect);
    selectCallback.current = onSelect;

    useEffect(() => {
        const item = items[activeIndex];

        if (item) {
            selectCallback.current?.(item, activeIndex);
        }
    }, [activeIndex, items]);

    useEffect(() => {
        const node = rootRef.current;

        if (!node) {
            return;
        }

        const observer = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const actions: Record<string, () => void> = {
            ArrowLeft: () => stepBy(-1),
            ArrowUp: () => stepBy(-1),
            ArrowRight: () => stepBy(1),
            ArrowDown: () => stepBy(1),
            Home: () => goToIndex(0, true),
            End: () => goToIndex(items.length - 1, true),
            Enter: () => items[activeIndex] && window.location.assign(items[activeIndex].href),
        };
        const action = actions[event.key];

        if (action) {
            event.preventDefault();
            action();
        }
    };

    // Only horizontal gestures (trackpad swipe, shift+wheel) spin the ring; vertical wheel keeps scrolling the page.
    const lastWheel = useRef(0);
    const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
        const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
        const now = performance.now();

        if (Math.abs(horizontal) < 4 || now - lastWheel.current < 140) {
            return;
        }

        lastWheel.current = now;
        stepBy(horizontal > 0 ? 1 : -1);
    };

    // Every tile is a link; only swallow the click that ends a drag.
    const handleTileClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (consumeDragClick()) {
            event.preventDefault();
        }
    };

    const active = items[activeIndex];
    const centerOpacity = useTransform(position, (latest) => Math.max(0, 1 - Math.abs(latest - Math.round(latest)) * 4));
    const propSize = Math.max(46.92, ring.rx * 0.20976);

    const style = {
        '--tile-w': `${layout.tileW}px`,
        '--tile-h': `${layout.tileH}px`,
        '--ring-pad': `${ring.pad}px`,
        '--ring-w': `${ring.rx * 2}px`,
        '--ring-ry': `${ring.ry}px`,
        '--ring-radius': `${layout.radius}px`,
        '--wall-h': `${ring.wall}px`,
        '--tilt': `${layout.tilt}deg`,
        height: `${ring.height}px`,
    } as CSSProperties;

    if (items.length === 0) {
        return null;
    }

    const podium = { rx: ring.rx, ry: ring.ry, wall: ring.wall, pad: ring.pad };
    const propLayer = {
        itemCount: items.length,
        activeIndex,
        position,
        rx: ring.rx,
        flatten,
        itemAngle: layout.itemAngle,
        slotCount: layout.slotCount,
        size: propSize,
        assetBase,
    };

    return (
        <div
            ref={setRefs}
            className={`wop-orbit${isDrifting ? ' is-drifting' : ''}`}
            style={style}
            role="listbox"
            aria-label={label}
            aria-activedescendant={active ? `wop-orbit-item-${active.id}` : undefined}
            tabIndex={0}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={handleKeyDown}
            onWheel={handleWheel}
        >
            <div className="wop-orbit__reflection" aria-hidden="true">
                <Podium layer="front" {...podium} />
            </div>
            <div className="wop-orbit__reflection-sheen" aria-hidden="true" />
            <Podium className="wop-orbit__podium wop-orbit__podium--back" layer="back" {...podium} />
            <PropLayer layer="back" {...propLayer} />
            <Podium className="wop-orbit__podium wop-orbit__podium--front" layer="front" {...podium} />
            <PropLayer layer="front" {...propLayer} />

            <div className="wop-orbit__stage">
                <div className="wop-orbit__ring">
                    {Array.from({ length: layout.slotCount }, (_, slot) => {
                        const index = slot % items.length;

                        return (
                            <Tile
                                key={slot}
                                item={items[index]}
                                slot={slot}
                                position={position}
                                layout={layout}
                                isActive={index === activeIndex}
                                isPrimary={slot < items.length}
                                onClick={handleTileClick}
                            />
                        );
                    })}

                    {active && (
                        <motion.a
                            href={active.href}
                            className="wop-orbit__tile wop-orbit__tile--center is-active"
                            style={{ opacity: centerOpacity }}
                            aria-label={active.title}
                            draggable={false}
                            onClick={handleTileClick}
                        >
                            <TileFace item={active} />
                        </motion.a>
                    )}
                </div>
            </div>
        </div>
    );
});
